import type { ResponsiveProp } from '@/lib/device/useBreakpoint'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import type { AnyTracking } from '@/lib/tracking/services/tracking.eventTypes'
import type { ButtonSize } from '@/ui/buttons/button-styles/ButtonStyles'
import type { IconProps } from '@/ui/icons/icon/Icon'
import type { LinkProps as NextLinkProps } from 'next/link'
import type { AriaAttributes, MouseEventHandler, PropsWithChildren, ReactElement } from 'react'
import type { LinkButtonVariant } from './linkVariantUtils'

export type LinkTint = 'brand-1' | 'brand-2'
export type TextLinkUnderlined = 'invert' | 'hover' | 'none'

type BaseProps = StrictOmit<NextLinkProps, 'href'> &
  UiComponentProps<{
    href: NextLinkProps['href']
    opensInNewTab?: boolean
    target?: string
    rel?: string
    tabIndex?: number
    onPress?: MouseEventHandler<HTMLAnchorElement>
    trackingData?: AnyTracking
    intent?: WebViewIntent
    'aria-current'?: AriaAttributes['aria-current']
  }>

export type TextLinkProps = PropsWithChildren<
  BaseProps & {
    variant: 'free'
    underlined?: TextLinkUnderlined
    bold?: boolean
    tint?: LinkTint
    icon?: ReactElement
    title?: string
    iconLeft?: never
    iconRight?: never
    isDisabled?: never
    isLoading?: never
    isRound?: never
    size?: never
    takesParentWidth?: never
    forceProgressBar?: never
    notification?: never
    notificationClassName?: never
  }
>

export type LinkButtonProps = BaseProps & {
  variant?: LinkButtonVariant
  children?: string
  underlined?: boolean
  bold?: true
  iconLeft?: ReactElement<IconProps>
  iconRight?: ReactElement<IconProps>
  isDisabled?: boolean
  isLoading?: boolean
  isRound?: ResponsiveProp<boolean>
  size?: ResponsiveProp<ButtonSize>
  takesParentWidth?: true
  forceProgressBar?: boolean
  notification?: boolean
  notificationClassName?: string
  tint?: never
  icon?: never
  title?: never
}

export type LinkProps = TextLinkProps | LinkButtonProps

export function isLinkButton(props: LinkProps): props is LinkButtonProps {
  return props.variant !== 'free'
}
